import styled from "styled-components";
import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Text from "@/components/Text";
import {
  InputGroup,
  ResetButton,
  ResetHolder,
  Wrapper,
} from "@/pages/AssignTopics/components/Filter";
import { selectTeamSettings } from "@/selectors";
import { Dispatch, RootState } from "@/utils/store";
import useDebounce from "@/hooks/useDebounce";
import useDidUpdate from "@/hooks/useDidUpdate";
import {
  ensureStringContainsDecimalNumbersOnly,
  ensureStringContainsPositiveDigitsOnly,
} from "@/utils/helpers";
import { Input } from "@/components/Forms";

const StyledText = styled(Text)`
  font-size: 11px;
`;

const Wrap = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
`;

const StyledInputGroup = styled(InputGroup)`
  margin: 0;
  width: 100%;
`;

const StyledInput = styled(Input)`
  height: 30px;
  border: 0;
  font-size: 11px;
  font-weight: 700;
  padding: 0 12px;
`;

interface State {
  minViews: string;
  minDuration: string;
  minAge: string;
  minV7Index: string;
}

const TopThumbnailsFilter = () => {
  const dispatch = useDispatch<Dispatch>();

  const { teamId } = useParams();

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!)
  );

  const [values, setValues] = useState<State>({
    minViews: teamSettings?.top_thumbnails_min_views ?? "",
    minDuration: teamSettings?.top_thumbnails_min_duration ?? "",
    minAge: teamSettings?.top_thumbnails_min_age ?? "",
    minV7Index: teamSettings?.top_thumbnails_min_v7_index ?? "",
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name !== "minV7Index" && !ensureStringContainsPositiveDigitsOnly(value))
      return;

    if (name === "minV7Index" && !ensureStringContainsDecimalNumbersOnly(value))
      return;

    setValues((prev) => ({ ...prev, [name as keyof State]: value }));
  };

  const debouncedValues = useDebounce(values, 500);

  const resetValue = (key: keyof State) =>
    setValues((prev) => ({ ...prev, [key]: "" }));

  useDidUpdate(() => {
    dispatch.teams.updateSettings({
      teamId: teamId!,
      updatedSettings: {
        top_thumbnails_min_views: debouncedValues.minViews,
        top_thumbnails_min_duration: debouncedValues.minDuration,
        top_thumbnails_min_age: debouncedValues.minAge,
        top_thumbnails_min_v7_index: debouncedValues.minV7Index,
      },
    });
  }, [debouncedValues, teamId]);

  return (
    <Wrap>
      <StyledInputGroup>
        <StyledText $type="smallBold" $color="black">
          Minimum views
        </StyledText>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("minViews")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minViews"
            value={values.minViews}
            onChange={onChange}
          />
        </Wrapper>
      </StyledInputGroup>
      <StyledInputGroup>
        <StyledText $type="smallBold" $color="black">
          Minimum duration (sec)
        </StyledText>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("minDuration")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minDuration"
            value={values.minDuration}
            onChange={onChange}
          />
        </Wrapper>
      </StyledInputGroup>
      <StyledInputGroup>
        <StyledText $type="smallBold" $color="black">
          Minimum age (days)
        </StyledText>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("minAge")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minAge"
            value={values.minAge}
            onChange={onChange}
          />
        </Wrapper>
      </StyledInputGroup>
      <StyledInputGroup>
        <StyledText $type="smallBold" $color="black">
          Minimum V7 Index (%)
        </StyledText>
        <Wrapper>
          <ResetHolder>
            <ResetButton onClick={() => resetValue("minV7Index")}>
              Reset
            </ResetButton>
          </ResetHolder>
          <StyledInput
            placeholder="Enter number"
            name="minV7Index"
            value={values.minV7Index}
            onChange={onChange}
          />
        </Wrapper>
      </StyledInputGroup>
    </Wrap>
  );
};

export default TopThumbnailsFilter;
